import { isDesktopRuntime } from '@/desktop/desktopDetection';
import type {
  EstimateScaleType,
  IProject,
  UpdatableWorkAreaFieldType,
  WorkAreaDetailKeyType,
} from '@featit/shared';
import { API_BASE_URL } from '@/services/ApiService';
import { projectApi } from '@/services/ProjectApi';
import { listDesktopWorkAreaBindings } from '@/desktop/desktopRuntimeLazy';
import type { IDesktopWorkAreaBinding } from '@/desktop/desktopRuntime';
import { createDesktopCloudApiTransport } from '@/desktop/desktopCloudTransport';
import { createProjectApi } from '@/services/ProjectApi';

export const WORK_AREA_DATA_SOURCE_KINDS = {
  cloud: 'cloud',
} as const;

export type WorkAreaDataSourceKindType =
  (typeof WORK_AREA_DATA_SOURCE_KINDS)[keyof typeof WORK_AREA_DATA_SOURCE_KINDS];

export const WORK_AREA_DATA_SOURCE_AVAILABILITY = {
  ready: 'ready',
  servicePending: 'service_pending',
} as const;

export type WorkAreaDataSourceAvailabilityType =
  (typeof WORK_AREA_DATA_SOURCE_AVAILABILITY)[keyof typeof WORK_AREA_DATA_SOURCE_AVAILABILITY];

export interface IWorkAreaDataSourceDescriptor {
  sourceId: string;
  kind: WorkAreaDataSourceKindType;
  availability: WorkAreaDataSourceAvailabilityType;
  project: IProject;
  apiBaseUrl: string | null;
  desktopBinding: IDesktopWorkAreaBinding | null;
}

export interface ICreateCloudWorkAreaInput {
  name: string;
  slug: string;
  description?: string;
  parentId?: string | null;
  visibility?: string;
  workAreaTypeId?: string;
  /** F-0338: whether a Public work area publishes every item unless marked confidential. */
  publishByDefault?: boolean;
  /** The namespace to create it in (F-0221). Omitted means the caller's own personal namespace. */
  namespaceId?: string | null;
}

interface IWorkAreaUpdateFields {
  name?: string;
  slug?: string;
  description?: string;
  policies?: Record<string, unknown>;
  parentId?: string | null;
  visibility?: string;
  workAreaTypeId?: string | null;
  /** F-0338: whether a Public work area publishes every item unless marked confidential. */
  publishByDefault?: boolean;
  /** F-0338 AC-PV17: offer this Public work area to search engines (needs `work_area.publish`). */
  indexable?: boolean;
  /** T-0502 SZ-9: how the work area's estimates read, or null for none (`work_area.manage`). */
  estimateScale?: EstimateScaleType | null;
  /** I-1192 (§5p KL-15): read `ADR-`, `AC-`, `R-` … against FEATit's documentation index. */
  linkDocumentReferences?: boolean;
}

/**
 * What a work area's general update may send (I-0751). The server refuses any field outside
 * `UPDATABLE_WORK_AREA_FIELDS` with a 400, so the input is picked from that list, and an object
 * literal naming another field does not compile. A change set held in a variable is not checked that
 * way; `work-area-update-allowlist.e2e-spec.ts` sends each screen's shape to the real server.
 */
export type IUpdateWorkAreaInput = Omit<
  Pick<IWorkAreaUpdateFields, UpdatableWorkAreaFieldType>,
  // I-0812: the web never writes `policies` whole. Every screen sends `policiesPatch` with only what
  // the person changed, so a field it merely displayed cannot change; this keeps it that way.
  'policies'
> & {
  /**
   * F-0288: the revision of the policies the save was composed from. `ProjectStore.updateProject`
   * sets it; a screen does not.
   */
  expectedPoliciesRevision?: string;
  /**
   * I-1792: the details the save was composed from, as the screen read them; the server refuses the
   * save, naming what moved, when one is no longer stored. A screen that has no basis sends none.
   */
  expectedDetails?: Partial<Record<WorkAreaDetailKeyType, unknown>>;
  /**
   * I-0795: only the policies fields that change, as a merge patch (`applyPoliciesPatch`). The
   * server applies it to what it stores, so a field the save does not name keeps its value. Never
   * sent beside `policies`.
   */
  policiesPatch?: Record<string, unknown>;
  /**
   * F-0338 AC-PV3: what the owner was shown the flip to Public publishes. Required by the server
   * to make a work area Public; refused when stale; never stored.
   */
  confirmCounts?: { items: number; comments: number; attachments: number };
};

interface IWorkAreaDataSourceRegistryDependencies {
  fetchCloudWorkAreas: () => Promise<IProject[]>;
  createCloudWorkArea: (input: ICreateCloudWorkAreaInput) => Promise<IProject>;
  updateCloudWorkArea: (id: string, input: IUpdateWorkAreaInput) => Promise<IProject>;
  desktopRuntimeAvailable: () => boolean;
  fetchDesktopBindings: () => Promise<IDesktopWorkAreaBinding[]>;
  fetchDesktopCloudWorkArea: (binding: IDesktopWorkAreaBinding) => Promise<IProject>;
}

const DEFAULT_DEPENDENCIES: IWorkAreaDataSourceRegistryDependencies = {
  fetchCloudWorkAreas: () => projectApi.findAll(),
  createCloudWorkArea: (input) => projectApi.create(input),
  updateCloudWorkArea: (id, input) => projectApi.update(id, input),
  desktopRuntimeAvailable: isDesktopRuntime,
  fetchDesktopBindings: listDesktopWorkAreaBindings,
  fetchDesktopCloudWorkArea: (binding) => {
    if (!binding.cloudWorkAreaId) throw new Error('The cloud work-area ID is unavailable.');
    return createProjectApi(createDesktopCloudApiTransport(binding.desktopWorkAreaId)).findById(
      binding.cloudWorkAreaId,
    );
  },
};

export class WorkAreaDataSourceRegistry {
  private descriptorsByProjectId = new Map<string, IWorkAreaDataSourceDescriptor>();

  constructor(
    private readonly dependencies: IWorkAreaDataSourceRegistryDependencies = DEFAULT_DEPENDENCIES,
  ) {}

  async discover(): Promise<IWorkAreaDataSourceDescriptor[]> {
    const desktopBindings = await this.desktopBindings();
    const cloudResult = await this.dependencies.fetchCloudWorkAreas();
    if (!Array.isArray(cloudResult)) {
      throw new Error('The work-area service returned an invalid response.');
    }
    const cloudProjects = cloudResult;
    const desktopCloudBindings = desktopBindings.filter(
      (binding): binding is IDesktopWorkAreaBinding & { cloudWorkAreaId: string } =>
        binding.storageMode === WORK_AREA_DATA_SOURCE_KINDS.cloud &&
        Boolean(binding.cloudWorkAreaId),
    );
    const desktopCloudProjects = await Promise.all(
      desktopCloudBindings.map(async (binding) => {
        try {
          return {
            binding,
            project: await this.dependencies.fetchDesktopCloudWorkArea(binding),
            availability: WORK_AREA_DATA_SOURCE_AVAILABILITY.ready,
          };
        } catch {
          return {
            binding,
            project: projectFromCloudBinding(binding),
            availability: WORK_AREA_DATA_SOURCE_AVAILABILITY.servicePending,
          };
        }
      }),
    );
    const cloudBindings = new Map(
      desktopCloudBindings.map((binding) => [binding.cloudWorkAreaId, binding]),
    );

    const descriptors: IWorkAreaDataSourceDescriptor[] = cloudProjects.map((project) => {
      // I-1917: only a binding on a custom connection (ADR-153) routes a work area the main
      // connection serves. A binding with no connection is the signed-in session's — Settings ›
      // Folders makes one to keep this Mac's folders under — and its address is the deployment's
      // origin, not its API: sent through it, every read of the work area reached the web server.
      const bound = cloudBindings.get(project.id) ?? null;
      const binding = bound?.connectionId ? bound : null;
      return {
        sourceId: binding?.desktopWorkAreaId ?? `cloud:${project.id}`,
        kind: WORK_AREA_DATA_SOURCE_KINDS.cloud,
        availability: WORK_AREA_DATA_SOURCE_AVAILABILITY.ready,
        project,
        apiBaseUrl: binding?.apiBaseUrl ?? API_BASE_URL,
        desktopBinding: binding,
      } satisfies IWorkAreaDataSourceDescriptor;
    });
    const knownCloudProjectIds = new Set(descriptors.map(({ project }) => project.id));
    for (const { binding, project, availability } of desktopCloudProjects) {
      if (knownCloudProjectIds.has(project.id)) continue;
      descriptors.push({
        sourceId: binding.desktopWorkAreaId,
        kind: WORK_AREA_DATA_SOURCE_KINDS.cloud,
        availability,
        project,
        apiBaseUrl: binding.apiBaseUrl,
        desktopBinding: binding,
      });
    }

    this.descriptorsByProjectId = new Map(
      descriptors.map((descriptor) => [descriptor.project.id, descriptor]),
    );
    return descriptors;
  }

  descriptor(projectId: string): IWorkAreaDataSourceDescriptor | undefined {
    return this.descriptorsByProjectId.get(projectId);
  }

  async createCloudWorkArea(input: ICreateCloudWorkAreaInput): Promise<IProject> {
    const project = await this.dependencies.createCloudWorkArea(input);
    this.descriptorsByProjectId.set(project.id, cloudDescriptor(project));
    return project;
  }

  async updateWorkArea(projectId: string, input: IUpdateWorkAreaInput): Promise<IProject> {
    const descriptor = this.descriptor(projectId);
    const project = await this.dependencies.updateCloudWorkArea(projectId, input);
    this.descriptorsByProjectId.set(project.id, {
      ...cloudDescriptor(project),
      desktopBinding: descriptor?.desktopBinding ?? null,
      apiBaseUrl: descriptor?.apiBaseUrl ?? API_BASE_URL,
    });
    return project;
  }

  private async desktopBindings(): Promise<IDesktopWorkAreaBinding[]> {
    if (!this.dependencies.desktopRuntimeAvailable()) return [];

    try {
      return await this.dependencies.fetchDesktopBindings();
    } catch {
      // A damaged desktop registry must not make otherwise healthy cloud projects disappear.
      return [];
    }
  }
}

function cloudDescriptor(project: IProject): IWorkAreaDataSourceDescriptor {
  return {
    sourceId: `cloud:${project.id}`,
    kind: WORK_AREA_DATA_SOURCE_KINDS.cloud,
    availability: WORK_AREA_DATA_SOURCE_AVAILABILITY.ready,
    project,
    apiBaseUrl: API_BASE_URL,
    desktopBinding: null,
  };
}

function projectFromCloudBinding(
  binding: IDesktopWorkAreaBinding & { cloudWorkAreaId: string },
): IProject {
  return {
    id: binding.cloudWorkAreaId,
    name: binding.name,
    slug: binding.slug,
    description: binding.description ?? '',
    policies: binding.policies ?? {},
    visibility: 'private',
    ownerId: 'desktop-cloud',
    createdAt: binding.createdAt,
    updatedAt: binding.updatedAt,
  };
}

export const workAreaDataSourceRegistry = new WorkAreaDataSourceRegistry();
